/**
 * results/resultActions.ts — what a person can DO from a result card.
 *
 * Until now every card in this directory was inert by design, and said so:
 * "THIS CARD DOES NOT ACT", "There is no Save button on this card". That was
 * right while the only thing a card could offer was a shortcut to a change,
 * because a change belongs to the confirmation ceremony and not to a tile in
 * the middle of an answer.
 *
 * The dashboard preview is what changed the picture. Once a preview DRAWS the
 * dashboard — real figures, real axes — the person looking at it has exactly
 * two things to say next: keep it, or change it. Making them retype "save
 * this" under a picture of the thing they want to save is the same
 * indirection the preview was built to remove.
 *
 * WHAT AN ACTION IS, AND EMPHATICALLY IS NOT. A {@link ResultAction} is a
 * REQUEST, described in the product's own vocabulary. It executes nothing,
 * authorizes nothing, and reaches no data. The surface turns it into the next
 * conversational turn; the server re-plans, re-authorizes, and — for a save —
 * issues the confirmation the person still has to give. Pressing [Save to
 * Dashboards] does not save a dashboard, exactly as typing "save this" does
 * not: it asks.
 *
 * AN ACTION IS A LOCAL VALUE, NOT A WIRE SHAPE (F264). Nothing on it crosses
 * the network. `resultActionMessage` reduces it to one SENTENCE, and the
 * sentence is the whole request — `intelligence-turn-results.test.tsx` asserts
 * the exact body.
 *
 * AND AN ACTION NOW CARRIES ONLY WHAT THE SENTENCE NEEDS (F290). Every action
 * below used to also carry `planId`, and the save action a `diffDigest`, put
 * there by the card that drew the button. Nothing ever read them.
 *
 * They were kept, twice, as the seam the surface WOULD bind with if an action
 * ever had to name the exact preview it came from — and that reason does not
 * survive being checked. `planId` is CONTENT-DERIVED: it is the first 128 bits
 * of `diffDigest`, re-shaped as a uuid (`dashboards/digest.ts`
 * `planIdFromDigest`). It names a SPEC, not a preview. Two previews of the
 * same dashboard drawn ten minutes apart carry the identical pair, so even
 * fully wired the seam could not answer the only question it was being kept
 * for — "did this button come from a preview that is now stale?" — it could
 * answer only "has the spec changed?", which the server already answers
 * better, from its own re-plan, in `dashboard.create`'s
 * `assertConfirmationBinds`. A seam that cannot carry the signal it is kept
 * for is not a seam; it is a field with a story attached.
 *
 * And the story is the cost. A field nothing reads has no test that can
 * contradict a wrong account of it, so the account drifts — which is not
 * hypothetical here. The doc on these very fields asserted, in production for
 * a release, that they "ride along so the follow-up turn is about this
 * preview" and that a stale button "therefore failed a digest binding". Both
 * were false, and F264 had to come back and say so. Keeping the fields keeps
 * that surface; keeping them PLUS a comment saying they are unread is a
 * comment to maintain against a field with no other reason to exist.
 *
 * What actually protects a person from a stale click is unchanged and is not
 * here: the sentence names the dashboard, and nothing is written without a
 * confirmation whose summary describes what the SERVER planned. On staging,
 * across four usable observations, the confirmation named the plan the button
 * was drawn from every time — including once with a different dashboard
 * previewed in between — while these fields sat unread. If a future action
 * ever does need to name a preview INSTANCE, the work is a server-issued
 * per-preview token and a structured request path replacing this sentence, not
 * reading a field that happened to be left lying here.
 *
 * WHY A PROP AND NOT A CONTEXT. There is ONE surface that owns the turn
 * mutation — `global/ConversationSurface.tsx`, which both the Ask Phibian page
 * and the global panel render (F224) — and it already passes the results array
 * down one level. An optional callback threaded the same way keeps the data and
 * the actions on one path; a context would put the handler somewhere the reader
 * of `ResultBlocks` cannot see it.
 *
 * WHAT "OPTIONAL" COST, AND WHAT NOW GUARDS IT (F258 → F262). Optional means a
 * card renders without buttons rather than with buttons that do nothing — which
 * is right, and which is also exactly how this shipped unreachable: the prop was
 * wired by NO host for an entire release, and the cards' own tests exercised both
 * branches of it, so the feature read as covered while nobody could press
 * anything. A component contract test cannot see that nobody calls it. The
 * standing answer is two-layered and both layers are required for any new gated
 * affordance: a HOST-level test that drives a real screen
 * (`intelligence-turn-results.test.tsx`, `intelligence-global-panel.test.tsx`),
 * and the census in `apps/admin/test/unit/gated-affordances.test.ts`, which finds
 * this shape in the source and fails if a production host stops supplying it.
 */

/**
 * The person wants to KEEP the dashboard they are looking at.
 *
 * The name is the whole of it: {@link resultActionMessage} says "save the
 * dashboard called X", which is what the person would have typed, and the save
 * turn re-plans from that. The plan identity that used to sit here is the
 * server's and stays the server's — see the F290 note above.
 */
export interface SaveDashboardAction {
  readonly kind: 'save_dashboard';
  /** The dashboard's name, so the surface can say what was asked for. */
  readonly name: string;
}

/** The person wants to CHANGE the dashboard before deciding about it. */
export interface EditDashboardAction {
  readonly kind: 'edit_dashboard';
  /** The dashboard's name, so the surface can say which one. */
  readonly name: string;
}

/** The person wants to look at the proposed automation properly before deciding. */
export interface ReviewAutomationAction {
  readonly kind: 'review_automation';
  /** The rule's name, in the words the automation builder uses. */
  readonly name: string;
}

export type ResultAction = SaveDashboardAction | EditDashboardAction | ReviewAutomationAction;

/**
 * The action as a sentence the person could have typed themselves.
 *
 * A surface with nothing more specific to do can send this as the next turn's
 * message and get exactly the behaviour the button promises — which is the
 * point: the button is a shortcut through the conversation, not a private
 * back channel around it. Keeping the phrasing here means the two surfaces
 * cannot drift into asking for the same thing two different ways.
 */
export function resultActionMessage(action: ResultAction): string {
  switch (action.kind) {
    case 'save_dashboard':
      return `Save the dashboard "${action.name}" to my dashboards.`;
    case 'edit_dashboard':
      return `I'd like to change the dashboard "${action.name}" before saving it.`;
    case 'review_automation':
      return `Walk me through the proposed automation "${action.name}" before I decide.`;
    default:
      return '';
  }
}
